import { Badge, Button, buttonClass, cardClass, fieldClass, SegmentedControl } from '@ledgerloom/ui';
import { useEffect, useId, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi, usePageTitle } from '../hooks';
import { can, isAdmin, useSession } from '../session';
import { Empty, ErrorBanner, Icon, ListSkeleton, Time, ToneBadge } from '../components/ui';
import { AskAgent } from '../components/AskAgent';
import { Face } from '../components/TeamFace';
import { label, sentence, useTeam, OUTCOME_WORD, type Member, type Motion } from './Team';
import { inboxItem, type Inbox } from './Approvals';
import { Tabs } from '../components/Tabs';
import { Band, Exchanges, PRESETS, TileMarks, counterparts, marksByAgent, windowOf, type Exchange, type Marks, type Traffic } from './DeskTraffic';

// The Agent Desk (ADR 0039): the Team and the Inbox as one command center. Its
// head links to what needs you, with the count. The team is the agents. Top to bottom:
// the map (each group's agents as tiles, then the agents in no group, unlabelled),
// workflows as rows of their steps, what you have going, and what is in motion;
// a section with nothing in it is not shown. An agent opens in a side panel: its status from
// its rows, then a conversation with it. Patterns: data-display/card for the
// group cards and tiles, navigation/sidebar-drawer for the panel (a full sheet at
// phone width), ai-intelligence/ai-chat for the conversation, data-display/list-view
// for the rows. Every list refreshes while the page is open.

interface DeskMap {
  groups: Array<{ id: string; name: string; purpose: string | null; agent_ids: string[] }>;
  workflows: Array<{ id: string; title: string; status: 'active' | 'paused' | null; steps: Array<{ id: string; title: string; agent_id: string; agent_name: string | null; running: number }> }>;
}
interface GoingRow { id: string; title: string; who: string; state: string; href: string | null; at: string }
interface AgentNow extends Member {
  queue: Array<{ id: string; what: string; state: 'queued' | 'claimed' | 'waiting'; at: string }>;
  for_you: Array<{ id: string; title: string }>;
  last_run: { id: string; status: string; summary: string | null; started_at: string; ended_at: string | null } | null;
  workflows: Array<{ id: string; title: string; step: string; running: number }>;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const FLOW_WORD: Record<string, string> = { active: 'On', paused: 'Paused' };
const TASK_STATE: Record<AgentNow['queue'][number]['state'], string> = { queued: 'waiting its turn', claimed: 'working on it now', waiting: 'waiting on another agent' };
const RUN_WORD: Record<string, string> = { succeeded: 'finished', failed: 'failed', running: 'is running', refused: 'was refused', stopped: 'stopped' };

// The page's lists refresh on their own so a lane change or a new item shows without a reload.
function useLive<T>(path: string | null, every = 15_000) {
  const r = useApi<T>(path, [path]);
  const reload = r.reload;
  useEffect(() => { if (!path) return undefined; const t = setInterval(() => void reload(), every); return () => clearInterval(t); }, [path, reload, every]);
  return r;
}

export function Desk() {
  const { me } = useSession();
  usePageTitle('Agent Desk');
  const team = useTeam(0);
  const map = useLive<DeskMap>('/v1/desk');
  const [params, setParams] = useSearchParams();
  const open = params.get('agent');
  const inbox = can(me, 'read:inbox');
  const members = team.data?.members ?? [];
  const agents = members.filter((m) => m.kind === 'agent');
  const byId = new Map(agents.map((a) => [a.id, a]));
  const groups = (map.data?.groups ?? []).filter((g) => g.agent_ids.length);
  const grouped = new Set(groups.flatMap((g) => g.agent_ids));
  const loose = agents.filter((a) => !grouped.has(a.id));
  const working = agents.filter((a) => a.status === 'active').length;
  const canHire = isAdmin(me) || me.role === 'manager';
  const last = useRef<string | null>(null);
  // The traffic layer (docs/design/desk-traffic.md): the exchanges in the window the URL names.
  const traffic = can(me, 'read:threads');
  const win = windowOf(params);
  const flow = useLive<Traffic>(traffic ? `/v1/desk/traffic?from=${encodeURIComponent(win.from.toISOString())}&to=${encodeURIComponent(win.to.toISOString())}&limit=500` : null);
  const items = flow.data?.items ?? [];
  const groupMembers = new Map((map.data?.groups ?? []).map((g) => [g.id, g.agent_ids]));
  const marks = marksByAgent(items, groupMembers);
  const near = open ? counterparts(open, items, groupMembers) : null;

  const choose = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('agent', id); else next.delete('agent');
    setParams(next);
  };
  const setWindow = (w: { preset: string } | { from: Date; to: Date }) => {
    const next = new URLSearchParams(params);
    next.delete('window'); next.delete('from'); next.delete('to');
    if ('preset' in w) { if (w.preset !== 'today') next.set('window', w.preset); } else { next.set('from', w.from.toISOString()); next.set('to', w.to.toISOString()); }
    setParams(next);
  };
  const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  // The picked dates are whole days: the last one ends at the midnight after it.
  const pickDays = (from: string, to: string) => { if (from && to) setWindow({ from: new Date(`${from}T00:00`), to: new Date(new Date(`${to}T00:00`).getTime() + 86_400_000) }); };
  // Closing the panel puts focus back on the tile that opened it.
  useEffect(() => {
    if (open) { last.current = open; return; }
    if (last.current) document.querySelector<HTMLElement>(`[data-agent="${CSS.escape(last.current)}"]`)?.focus();
  }, [open]);

  const tile = (id: string) => {
    const m = byId.get(id);
    return m ? <li key={id}><AgentTile m={m} on={open === id} marks={marks.get(id)} chip={near?.chips.get(id)} onOpen={() => choose(open === id ? null : id)} /></li> : null;
  };

  return (
    <div className={`desk${open ? ' panel-open' : ''}`}>
      <div className="desk-main stack">
        <div className="page-head">
          <div>
            <h1>Agent Desk</h1>
            <p>{team.data ? `${plural(working, 'agent')} working · updated live` : 'Your agents, what they are doing, and what needs you'}</p>
          </div>
          <div className="row">
            {inbox && <NeedsYouLink />}
            <Link className={buttonClass({ variant: 'secondary' })} to="/agents">Agents</Link>
            {canHire && <Link className={buttonClass()} to="/agents/hire"><Icon name="plus" />Hire an agent</Link>}
          </div>
        </div>

        <ErrorBanner error={team.error ?? map.error ?? flow.error} />
        {!team.data || !map.data ? <div className={cardClass} aria-busy="true"><ListSkeleton rows={4} label="Loading your team" /></div> : agents.length > 0 && (
          <section className="stack" style={{ gap: 12 }} aria-labelledby="desk-map-h">
            <div className="desk-section-head">
              <h2 id="desk-map-h">Your team</h2>
              {groups.length > 0 && <Link className={buttonClass({ variant: 'ghost', size: 'sm' })} to="/agents/groups">Groups</Link>}
            </div>
            {traffic && (
              <div className="desk-window" role="group" aria-label="Period">
                {/* A picked range selects no preset. */}
                <SegmentedControl label="Preset periods" value={win.preset ?? ''} onValueChange={(p) => setWindow({ preset: p })} segments={PRESETS.map(([p, text]) => ({ value: p, label: text }))} />
                <label className="small">From <input type="date" className={`${fieldClass} h-9 w-auto`} value={day(win.from)} max={day(new Date(win.to.getTime() - 1))} onChange={(e) => pickDays(e.target.value, day(new Date(win.to.getTime() - 1)))} /></label>
                <label className="small">To <input type="date" className={`${fieldClass} h-9 w-auto`} value={day(new Date(win.to.getTime() - 1))} min={day(win.from)} onChange={(e) => pickDays(day(win.from), e.target.value)} /></label>
                {/* shortcut: one page of 500 is read, so a busier period undercounts and says so; upgrade by paging on with before until the window is exhausted */}
                {flow.data && <span className="faint small">{items.length >= 500 ? 'The newest 500 exchanges shown: narrow the period to see them all' : items.length ? `${items.length} exchange${items.length === 1 ? '' : 's'} between agents` : 'No exchanges between agents in this period'}</span>}
              </div>
            )}
            <div className="desk-map">
              {groups.map((g) => (
                <section key={g.id} className={`${cardClass} desk-group`} aria-labelledby={`group-${g.id}`}>
                  <h3 id={`group-${g.id}`}><Link to={`/agents/groups/${g.id}`}>{g.name}</Link> <span className="faint">{g.agent_ids.length}</span></h3>
                  {g.purpose && <p className="faint small">{g.purpose}</p>}
                  <ul className="desk-tiles">{g.agent_ids.map(tile)}</ul>
                </section>
              ))}
              {loose.length > 0 && (
                <section className={`${cardClass} desk-group wide`} aria-label={groups.length ? 'Agents in no group' : 'Agents'}>
                  <ul className="desk-tiles">{loose.map((a) => tile(a.id))}</ul>
                </section>
              )}
            </div>
            {items.length > 0 && <Band items={items} from={win.from} to={win.to} onNarrow={(from, to) => setWindow({ from, to })} />}
          </section>
        )}

        {!!map.data?.workflows.length && <Workflows flows={map.data.workflows} open={open} onOpen={choose} />}
        {inbox && <Going />}
        {inbox && <MotionBand />}
      </div>
      {open && <AgentPanel key={open} id={open} exchanges={traffic ? near?.mine ?? [] : undefined} onClose={() => choose(null)} />}
    </div>
  );
}

// A tile's marks say what the agent sent and received in the period; when another tile is pressed,
// the chip says what passed between them, and the tile is lit.
function AgentTile({ m, on, marks, chip, onOpen }: { m: Member; on: boolean; marks?: Marks; chip?: string; onOpen: () => void }) {
  return (
    <button type="button" className={`desk-tile${on ? ' on' : ''}${chip ? ' near' : ''}`} data-agent={m.id} aria-expanded={on} aria-controls={on ? 'agent-panel' : undefined} onClick={onOpen}>
      <Face kind="agent" name={m.name} template={m.template_id} tone={m.tone} size={36} />
      <span className="desk-tile-text">
        <span className="desk-tile-name">{m.name}</span>
        <ToneBadge tone={m.tone}>{label(m)}</ToneBadge>
        <TileMarks m={marks} />
        {chip && <Badge tone="accent">{chip}</Badge>}
      </span>
    </button>
  );
}

// The way to what needs the person, with its count; the list itself is its own page.
function NeedsYouLink() {
  const inbox = useLive<Inbox>('/v1/inbox', 10_000);
  const n = inbox.data?.items.length;
  return <Link className={buttonClass({ variant: 'secondary' })} to="/desk/needs-you">Needs you{n ? <span className="faint"> {n}</span> : null}</Link>;
}

// Each workflow as its steps in order; a step's agent opens in the panel, and the runs standing at it show.
function Workflows({ flows, open, onOpen }: { flows: DeskMap['workflows']; open: string | null; onOpen: (id: string) => void }) {
  return (
    <section className="stack" style={{ gap: 12 }} aria-labelledby="desk-flows-h">
      <div className="desk-section-head">
        <h2 id="desk-flows-h">Workflows</h2>
        <Link className={buttonClass({ variant: 'ghost', size: 'sm' })} to="/agents/workflows">Open workflows</Link>
      </div>
      <ul className="desk-flows">
        {flows.map((w) => (
          <li key={w.id} className={`${cardClass} desk-flow`}>
            <div className="row" style={{ gap: 8 }}>
              <h3 className="desk-flow-title">{w.title}</h3>
              <Badge tone={w.status === 'active' ? 'success' : 'neutral'}>{w.status ? FLOW_WORD[w.status] : 'Not signed yet'}</Badge>
            </div>
            <ol className="desk-steps" aria-label={`Steps of ${w.title}`}>
              {w.steps.map((st) => (
                <li key={st.id} className={st.running ? 'running' : undefined}>
                  <button type="button" className={`desk-step${open === st.agent_id ? ' on' : ''}`} data-agent={st.agent_id} aria-expanded={open === st.agent_id} onClick={() => onOpen(st.agent_id)}>
                    <span className="desk-step-title">{st.title}</span>
                    <span className="faint small">{st.agent_name ?? 'An agent no longer hired'}</span>
                    {st.running > 0 && <ToneBadge tone="work">{plural(st.running, 'run')} here now</ToneBadge>}
                  </button>
                </li>
              ))}
            </ol>
          </li>
        ))}
      </ul>
    </section>
  );
}

// Open questions to agents, and what waits on someone else; shown only when there is some.
function Going() {
  const g = useLive<{ conversations: GoingRow[]; waiting: GoingRow[] }>('/v1/inbox/going');
  if (!g.data || (!g.data.conversations.length && !g.data.waiting.length)) return null;
  const parts: Array<[string, string, GoingRow[]]> = [['going-talk', 'Your conversations', g.data.conversations], ['going-wait', 'Waiting on others', g.data.waiting]];
  return (
    <div className="grid-2">
      {parts.map(([id, title, rows]) => rows.length > 0 && (
        <section key={id} className={`${cardClass} stack`} style={{ gap: 8, padding: 16 }} aria-labelledby={id}>
          <h2 id={id} className="desk-sub-h">{title} <span className="faint">{rows.length}</span></h2>
          <ul className="desk-rows">
            {rows.map((r) => {
              const body = <><span className="item-title">{r.title}</span><span className="item-sub">{r.who} · {r.state} · <Time iso={r.at} /></span></>;
              return <li key={r.id}>{r.href ? <Link className="desk-row" to={r.href}>{body}</Link> : <div className="desk-row">{body}</div>}</li>;
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

// Decided sign-offs whose work is still going, and what finished today; each opens on the In motion page.
function MotionBand() {
  const m = useLive<{ items: Motion[] }>('/v1/inbox/in-motion');
  const items = m.data?.items ?? [];
  if (!items.length) return null;
  return (
    <section className={`${cardClass} stack`} style={{ gap: 8, padding: 16 }} aria-labelledby="desk-motion-h">
      <div className="desk-section-head">
        <h2 id="desk-motion-h" className="desk-sub-h">In motion <span className="faint">{items.filter((i) => i.band === 'motion').length}</span></h2>
        <Link className={buttonClass({ variant: 'ghost', size: 'sm' })} to="/desk/in-motion">Open in motion</Link>
      </div>
      <ul className="desk-rows">
        {items.map((i) => (
          <li key={i.id}>
            <Link className="desk-row" to={`/desk/in-motion?item=${encodeURIComponent(i.id)}`}>
              <span className="item-title">{i.title}</span>
              <span className="item-sub">{i.band === 'motion' ? 'Still going' : 'Done today'} · {OUTCOME_WORD[i.decided.outcome] ?? i.decided.outcome} by {i.decided.by ?? 'someone'} · <Time iso={i.decided.at} /></span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// One agent: its name (to its page), its status read from its rows, then a conversation with it.
// In a side panel on the Desk, or as the right side of the chat page.
// With exchanges (the Desk's window), the panel has two tabs: the conversation, and the agent's exchanges.
export function AgentPanel({ id, onClose, page, exchanges }: { id: string; onClose?: () => void; page?: boolean; exchanges?: Exchange[] }) {
  const now = useLive<AgentNow>(`/v1/agents/${encodeURIComponent(id)}/now`);
  const headingId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const [tab, setTab] = useState<'talk' | 'exchanges'>('talk');
  const a = now.data;
  useEffect(() => { if (!page) heading.current?.focus(); }, [page]);
  const talk = (
    <>
      {!a ? (now.error ? null : <ListSkeleton rows={3} label="Loading its status" />) : <Status a={a} />}
      <AskAgent key={id} base={`/v1/agents/${encodeURIComponent(id)}`} title="Ask it" fallbackName="this agent" working="is answering"
        hint="{name} answers from its own work and only what you may see. It changes nothing."
        starters={['What do you do?', 'What have you found lately?']} onAnswered={() => void now.reload()} follow={!page && tab === 'talk'} />
    </>
  );
  return (
    <aside id="agent-panel" className={page ? `agent-pane ${cardClass}` : 'agent-panel'} aria-labelledby={headingId}
      onKeyDown={(e) => { if (e.key === 'Escape' && onClose) { e.stopPropagation(); onClose(); } }}>
      <header className="agent-panel-head">
        {a && <Face kind="agent" name={a.name} template={a.template_id} tone={a.tone} size={40} />}
        <div className="grow">
          <h2 id={headingId} ref={heading} tabIndex={-1}><Link to={`/agents/${encodeURIComponent(id)}`}>{a?.name ?? 'Agent'}</Link></h2>
          {a && <span className="faint small">{a.title ?? 'Agent'}</span>}
        </div>
        {!page && <Link className={buttonClass({ variant: 'ghost', size: 'sm' })} to={`/desk/chat/${encodeURIComponent(id)}`}>Open as page</Link>}
        {onClose && <Button type="button" variant="ghost" size="sm" onClick={onClose} aria-label="Close the panel"><Icon name="x" /></Button>}
      </header>
      <ErrorBanner error={now.error} />
      {exchanges ? (
        <Tabs label="About this agent" value={tab} tabs={[['talk', 'Conversation'], ['exchanges', `Exchanges${exchanges.length ? ` ${exchanges.length}` : ''}`]]} onChange={setTab}>
          {tab === 'talk' ? talk : <Exchanges agent={id} items={exchanges} />}
        </Tabs>
      ) : talk}
    </aside>
  );
}

// The agent's status as its first message: read from its rows, so it costs no model and is never a guess.
function Status({ a }: { a: AgentNow }) {
  // Tasks of one kind in one state read as one line with a count.
  const queue = [...a.queue.reduce((m, q) => m.set(`${q.what}|${q.state}`, { ...q, n: (m.get(`${q.what}|${q.state}`)?.n ?? 0) + 1 }), new Map<string, AgentNow['queue'][number] & { n: number }>()).values()];
  return (
    <section className="agent-status" aria-label={`${a.name}'s status`}>
      <p className="agent-status-lead"><ToneBadge tone={a.tone}>{label(a)}</ToneBadge> {sentence(a)}</p>
      <ul className="agent-status-list">
        <li>{queue.length ? <>In its queue: {queue.map((q, n) => <span key={q.id}>{n ? '; ' : ''}{q.what}{q.n > 1 ? ` (${q.n})` : ''}, {TASK_STATE[q.state]}</span>)}.</> : 'Nothing in its queue.'}</li>
        {a.for_you.length > 0 && (
          <li>Waiting on you:
            <ul>{a.for_you.map((i) => <li key={i.id}><Link to={inboxItem(i.id)}>{i.title}</Link></li>)}</ul>
          </li>
        )}
        <li>{a.last_run ? <>Last ran <Time iso={a.last_run.started_at} /> and {RUN_WORD[a.last_run.status] ?? a.last_run.status}.{a.last_run.summary && <span className="agent-status-summary">{a.last_run.summary}</span>}</> : 'It has not run yet.'}</li>
        {a.workflows.map((w) => <li key={`${w.id}:${w.step}`}>Does "{w.step}" in {w.title}{w.running ? `, ${plural(w.running, 'run')} there now` : ''}.</li>)}
      </ul>
    </section>
  );
}

// The conversation as a page: every active agent on the left, the chosen one on the right.
export function DeskChat() {
  const { id } = useParams<{ id: string }>();
  const team = useTeam(0);
  const agents = (team.data?.members ?? []).filter((m) => m.kind === 'agent' && m.status === 'active');
  const chatting = agents.find((a) => a.id === id)?.name;
  usePageTitle(chatting ? `Chat with ${chatting}` : 'Agents');
  return (
    <div className="desk-chat">
      <nav className={`desk-chat-list ${cardClass}`} aria-labelledby="desk-chat-h">
        <Link className={buttonClass({ variant: 'ghost', size: 'sm' })} to={id ? `/desk?agent=${encodeURIComponent(id)}` : '/desk'}><Icon name="back" />Agent Desk</Link>
        <h1 id="desk-chat-h" className="desk-sub-h">Agents</h1>
        <ErrorBanner error={team.error} />
        {!team.data ? <ListSkeleton rows={4} label="Loading your agents" /> : !agents.length ? <p className="faint small">No agent is working yet.</p> : (
          <ul>
            {agents.map((a) => (
              <li key={a.id}>
                <Link className={`desk-chat-item${a.id === id ? ' on' : ''}`} to={`/desk/chat/${encodeURIComponent(a.id)}`} aria-current={a.id === id ? 'page' : undefined}>
                  <Face kind="agent" name={a.name} template={a.template_id} tone={a.tone} size={28} />
                  <span className="grow">{a.name}</span>
                  <span className="sr-only">, {label(a)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </nav>
      {id ? <AgentPanel key={id} id={id} page /> : <div className={`${cardClass} agent-pane`}><Empty title="Choose an agent">Pick an agent on the left to see its status and ask it anything.</Empty></div>}
    </div>
  );
}
